import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import * as z from "zod";

import AdminRoutes from "Routes/Components/Admin";

import { HTTPErrorCallback } from "Helpers/Error";
import { cpfCnpjMask } from "Helpers/Format";
import { clearMask } from "Helpers/Util";

import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";
import ContainerWithTabs from "Components/Admin/ContainerWithTabs";
import FormWrapper from "Components/Admin/Form/FormWrapper";
import Input from "Components/Admin/Form/Input";
import Select from "Components/Admin/Form/Select";
import Loading from "Components/Admin/Loading";
import Section from "Components/Admin/Section";
import Table from "Components/Admin/Table";
import GetIcon from "Components/Global/Icons";

import { organizationTypeOptions } from "../constants";
import UserModal from "./UserModal";
import { usersColumns, projectsColumns } from "./columns";
import * as S from "./styles";

const DetailParticipant = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();

  const [participant, setParticipant] = useState(null);
  const [fetchingData, setFetchingData] = useState(true);
  const [modal, setModal] = useState({ open: false, user: null });

  // --- Dados da empresa ---
  const [cnpj, setCnpj] = useState("");
  // Campos com máscara são controlados (o Input mascarado lê o estado via prop
  // `value`, não pelo setValue do react-hook-form).
  const [phone, setPhone] = useState("");

  const companyFormSchema = z.object({
    name: z.string().min(3, "Nome deve ter ao menos 3 caracteres"),
    email: z.string().email("E-mail inválido").optional().or(z.literal("")),
    phone: z.string().optional().or(z.literal("")),
    website: z.string().optional().or(z.literal("")),
    description: z.string().optional().or(z.literal("")),
    // Dados da organização (opcionais).
    organization_type: z.string().optional().or(z.literal("")).nullable(),
    organization_area: z.string().optional().or(z.literal("")).nullable(),
    address: z.string().optional().or(z.literal("")).nullable(),
    neighbourhood: z.string().optional().or(z.literal("")).nullable(),
    city: z.string().optional().or(z.literal("")).nullable(),
    state: z.string().optional().or(z.literal("")).nullable(),
    cep: z.string().optional().or(z.literal("")).nullable(),
  });

  const companyMethods = useForm({ resolver: zodResolver(companyFormSchema) });
  const { register: registerCompany, setValue: setCompanyValue } =
    companyMethods;

  // ---

  useEffect(() => {
    if (id) getParticipant(id);
    // eslint-disable-next-line
  }, [id]);

  const getParticipant = (participantId) => {
    setFetchingData(true);
    api
      .post("/participants/get-by-id", { participant_id: participantId })
      .then((response) => {
        const data = response.data.participant;
        setParticipant(data);
        setCompanyValue("name", data.name);
        setCompanyValue("email", data.email || "");
        setCompanyValue("website", data.website || "");
        setCompanyValue("description", data.description || "");
        setCompanyValue("organization_type", data.organization_type || "");
        setCompanyValue("organization_area", data.organization_area || "");
        setCompanyValue("address", data.address || "");
        setCompanyValue("neighbourhood", data.neighbourhood || "");
        setCompanyValue("city", data.city || "");
        setCompanyValue("state", data.state || "");
        setCompanyValue("cep", data.cep || "");
        setCnpj(cpfCnpjMask(data.cnpj || ""));
        setPhone(data.phone || "");
      })
      .catch((error) => HTTPErrorCallback(error))
      .finally(() => setFetchingData(false));
  };

  const submitCompany = (data) => {
    setLoading(true);
    data.cnpj = clearMask(cnpj);
    data.phone = phone ? clearMask(phone) : "";

    api
      .post("/participants/update", { participant_id: id, participant: data })
      .then(() => {
        setLoading(false);
        showNotification(
          "success",
          "Sucesso!",
          "Empresa atualizada com sucesso!",
        );
        getParticipant(id);
      })
      .catch((error) => {
        setLoading(false);
        HTTPErrorCallback(error);
      });
  };

  if (fetchingData) return <Loading />;

  return (
    <Section>
      <S.Wrapper>
        {/* Botão voltar */}
        <S.Buttons>
          <Button
            type="button"
            text="Voltar"
            onClick={() => navigate(AdminRoutes.ListParticipants.path)}
            width="150px"
          />
          {/* {participant && (
            <ToggleStatus
              route="/participants/toggle-status/"
              item={participant}
              refresh={() => getParticipant(id)}
            />
          )} */}
        </S.Buttons>

        {/* Dados da Empresa */}
        <S.Card>
          <S.CardTitle>Dados da Empresa</S.CardTitle>
          <FormWrapper submit={submitCompany} {...companyMethods}>
            <S.FormGrid>
              <Input
                {...registerCompany("name")}
                type="text"
                label="Nome da Empresa:"
                placeholder="Razão Social"
                required
              />
              <Input
                name="cnpj"
                type="text"
                label="CNPJ:"
                placeholder="00.000.000/0000-00"
                value={cnpj}
                onChange={(e) => setCnpj(cpfCnpjMask(e.target.value))}
                required
              />
              <Input
                {...registerCompany("email")}
                type="email"
                label="E-mail Corporativo:"
                placeholder="contato@empresa.com"
              />
              <Input
                name="phone"
                type="text"
                label="Telefone:"
                placeholder="+55 (00) 00000-0000"
                mask="+99 (99) 99999-9999"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <Input
                {...registerCompany("website")}
                type="text"
                label="Website:"
                placeholder="https://empresa.com.br"
              />
              <Input
                {...registerCompany("description")}
                type="text"
                label="Descrição:"
                placeholder="Breve descrição da empresa"
              />
              <Select
                name="organization_type"
                label="Tipo de organização:"
                placeholder="Selecione"
                options={organizationTypeOptions}
              />
              <Input
                {...registerCompany("organization_area")}
                type="text"
                label="Ramo de atividade:"
                placeholder="Ramo de atividade da organização"
              />
              <Input
                {...registerCompany("cep")}
                type="text"
                label="CEP:"
                placeholder="00000-000"
              />
              <Input
                {...registerCompany("address")}
                type="text"
                label="Endereço:"
                placeholder="Logradouro"
              />
              <Input
                {...registerCompany("neighbourhood")}
                type="text"
                label="Bairro:"
                placeholder="Bairro"
              />
              <Input
                {...registerCompany("city")}
                type="text"
                label="Cidade:"
                placeholder="Cidade"
              />
              <Input
                {...registerCompany("state")}
                type="text"
                label="Estado:"
                placeholder="UF"
              />
            </S.FormGrid>
            <Button
              type="submit"
              text={loading ? "Salvando..." : "Salvar Alterações"}
              disabled={loading}
              width="200px"
            />
          </FormWrapper>
        </S.Card>

        {/* Abas: Usuários vinculados (padrão) e Projetos */}
        <ContainerWithTabs
          mt="20px"
          tabs={[
            {
              title: `Usuários Vinculados (${participant?.users?.length || 0})`,
              component: (
                <>
                  <S.Buttons>
                    <div />
                    <Button
                      type="button"
                      text="Adicionar Usuário"
                      onClick={() => setModal({ open: true, user: null })}
                      width="200px"
                    />
                  </S.Buttons>
                  <S.UsersTableWrapper>
                    <Table
                      name="Usuários"
                      data={participant?.users || []}
                      columns={[
                        ...usersColumns,
                        {
                          Header: "Ações",
                          accessor: "actions",
                          noSearch: true,
                          width: "70px",
                          Cell: ({ cell }) => {
                            const row = cell.row.original;
                            return (
                              <S.Actions>
                                <GetIcon
                                  name="Edit"
                                  onClick={() =>
                                    setModal({ open: true, user: row })
                                  }
                                />
                              </S.Actions>
                            );
                          },
                        },
                      ]}
                    />
                  </S.UsersTableWrapper>
                </>
              ),
            },
            {
              title: `Projetos (${participant?.projects?.length || 0})`,
              component: (
                <S.UsersTableWrapper>
                  <Table
                    name="Projetos"
                    data={participant?.projects || []}
                    columns={[
                      ...projectsColumns,
                      {
                        Header: "Ações",
                        accessor: "actions",
                        noSearch: true,
                        width: "70px",
                        Cell: ({ cell }) => {
                          const row = cell.row.original;
                          return (
                            <S.Actions>
                              <GetIcon
                                name="Edit"
                                onClick={() =>
                                  navigate(
                                    AdminRoutes.UpdateProjects.path.replace(
                                      ":id",
                                      row.id,
                                    ),
                                  )
                                }
                              />
                            </S.Actions>
                          );
                        },
                      },
                    ]}
                  />
                </S.UsersTableWrapper>
              ),
            },
          ]}
        />

        {modal.open && (
          <UserModal
            user={modal.user}
            participantId={id}
            onClose={() => setModal({ open: false, user: null })}
            onSuccess={() => getParticipant(id)}
          />
        )}
      </S.Wrapper>
    </Section>
  );
};

export default DetailParticipant;
